import React, { useState, useMemo } from 'react';
import { 
  Calculator, Calendar, Clock, TrendingUp, Users, Award, 
  RefreshCw, ChevronDown, ChevronUp, AlertTriangle, CheckCircle,
  ArrowRight, Briefcase, UserMinus, Info, Sparkles
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

// Helper function to calculate working days between two dates
const getWorkingDaysBetween = (startDate, endDate, daysPerWeek = 5) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  let count = 0;
  const current = new Date(start);
  
  while (current <= end) {
    const dayOfWeek = current.getDay();
    // For 5-day week, count Mon-Fri (1-5)
    if (daysPerWeek === 5 && dayOfWeek !== 0 && dayOfWeek !== 6) {
      count++;
    } else if (daysPerWeek === 6 && dayOfWeek !== 0) {
      count++;
    } else if (daysPerWeek === 7) {
      count++;
    } else if (daysPerWeek < 5) {
      // For part-time, approximate
      count++;
    }
    current.setDate(current.getDate() + 1);
  }
  return count;
};

// Get leave year dates based on start month
const getLeaveYearDates = (startMonth) => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  
  let yearStart, yearEnd;
  
  if (currentMonth >= startMonth) {
    yearStart = new Date(currentYear, startMonth - 1, 1);
    yearEnd = new Date(currentYear + 1, startMonth - 1, 0);
  } else {
    yearStart = new Date(currentYear - 1, startMonth - 1, 1);
    yearEnd = new Date(currentYear, startMonth - 1, 0);
  }
  
  return { yearStart, yearEnd };
};

// `bankHolidays`: the tenant's REAL public holidays for this year, from the backend
// (region-aware — E&W 8, Scotland 9, NI 10). `jurisdiction`: the tenant's
// employment-law facts from GET /settings/jurisdiction, or null when their country
// isn't modelled. NO law is hard-coded here (D9): null jurisdiction = every
// statutory section, tab and claim simply disappears — never UK numbers as default.
const LeaveCalculator = ({ leaveSettings, bankHolidays = [], jurisdiction = null }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState('breakdown');

  const annualLeaveRule = jurisdiction?.annual_leave || null;
  const jurisdictionName = jurisdiction?.jurisdiction || null;
  const bankHolidayCount = bankHolidays.length;
  // Parsed + sorted once: [{name, date: Date}]
  const bhList = useMemo(() => (
    bankHolidays
      .map((bh) => ({ name: bh.name, date: new Date(bh.date) }))
      .sort((a, b) => a.date - b.date)
  ), [bankHolidays]);
  
  // Pro-rata calculator state
  const [proRataStartDate, setProRataStartDate] = useState('');
  
  // Part-time calculator state
  const [partTimeDays, setPartTimeDays] = useState(5);
  
  // Leaver calculator state
  const [leavingDate, setLeavingDate] = useState('');
  const [daysTaken, setDaysTaken] = useState(0);

  // Get leave year info
  const leaveYear = useMemo(() => {
    return getLeaveYearDates(leaveSettings?.leave_year_start_month || 1);
  }, [leaveSettings?.leave_year_start_month]);

  // Statutory minimum for a 5-day full-timer — from the backend rule pack only.
  // null = no rule pack = no statutory comparison anywhere in this component.
  const statutoryMinimum = useMemo(() => {
    if (!annualLeaveRule) return null;
    return Math.min(5 * annualLeaveRule.weeks, annualLeaveRule.cap_days);
  }, [annualLeaveRule]);

  // Calculate service bonus
  const calculateServiceBonus = (yearsOfService) => {
    if (!leaveSettings?.service_bonus_enabled) return 0;
    
    if (leaveSettings?.service_bonus_use_tiers) {
      try {
        const tiers = JSON.parse(leaveSettings?.service_bonus_tiers || '[]');
        let totalBonus = 0;
        for (const tier of tiers) {
          if (yearsOfService >= tier.after_years) {
            totalBonus += tier.extra_days;
          }
        }
        return totalBonus;
      } catch {
        return 0;
      }
    } else {
      const eligibleYears = Math.max(0, yearsOfService - (leaveSettings?.service_bonus_min_years || 0));
      const bonus = eligibleYears * (leaveSettings?.service_bonus_days_per_year || 1);
      return Math.min(bonus, leaveSettings?.service_bonus_max_days || 5);
    }
  };

  // Full entitlement breakdown
  const entitlementBreakdown = useMemo(() => {
    const base = leaveSettings?.default_annual_allowance || 28;
    const bankHols = leaveSettings?.bank_holidays_included ? 0 : bankHolidayCount;
    const serviceBonus = calculateServiceBonus(3); // Example: 3 years service
    const carryOver = leaveSettings?.carry_over_enabled ? Math.min(5, leaveSettings?.carry_over_max_days || 5) : 0;
    
    return {
      base,
      bankHolidays: bankHols,
      serviceBonus,
      carryOver,
      total: base + bankHols + serviceBonus + carryOver
    };
  // `calculateServiceBonus` is recreated each render; including it would rebuild this on every pass.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaveSettings, bankHolidayCount]);

  // Pro-rata calculation (monthly accrual + round up)
  const proRataResult = useMemo(() => {
    if (!proRataStartDate) return null;
    
    const startDate = new Date(proRataStartDate);
    const { yearStart, yearEnd } = leaveYear;
    
    if (startDate < yearStart || startDate > yearEnd) {
      return { error: 'Start date must be within the current leave year' };
    }
    
    // Calculate months remaining (gov.uk method: count the starting month as a full month)
    const startMonth = startDate.getMonth(); // 0-indexed
    const yearStartMonth = yearStart.getMonth();
    const yearEndMonth = 11; // December
    
    // Months from start month to December (inclusive)
    const monthsRemaining = yearEndMonth - startMonth + 1;
    const totalMonths = 12;
    
    // Monthly pro-rata calculation
    const fullEntitlement = leaveSettings?.default_annual_allowance || 28;
    const monthlyProportion = monthsRemaining / totalMonths;
    const proRataBase = fullEntitlement * monthlyProportion;
    
    // Round UP (gov.uk says cannot round down)
    const proRataEntitlement = Math.ceil(proRataBase);
    
    // Calculate remaining bank holidays (only those after start date) — from the
    // tenant's REAL holiday data, not a hard-coded list.
    const remainingBankHolidays = bhList.filter(bh => bh.date >= startDate);
    const passedBankHolidays = bhList.filter(bh => bh.date < startDate);
    
    // Bank holidays handling
    const bankHolidaysIncluded = leaveSettings?.bank_holidays_included;
    const remainingBHCount = remainingBankHolidays.length;
    const passedBHCount = passedBankHolidays.length;
    
    // Total calculation
    let totalTimeOff;
    if (bankHolidaysIncluded) {
      // Bank holidays are part of the allowance - just show pro-rata total
      totalTimeOff = proRataEntitlement;
    } else {
      // Bank holidays are additional - add remaining ones
      totalTimeOff = proRataEntitlement + remainingBHCount;
    }
    
    return {
      // Input info
      startDateFormatted: startDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
      
      // Monthly calculation
      monthsRemaining,
      totalMonths,
      monthlyProportion: (monthlyProportion * 100).toFixed(1),
      
      // Base allowance
      fullEntitlement,
      proRataBase: proRataBase.toFixed(2),
      proRataEntitlement,
      
      // Bank holidays
      bankHolidaysIncluded,
      totalBankHolidays: bhList.length,
      remainingBankHolidays: remainingBHCount,
      passedBankHolidays: passedBHCount,
      remainingBHList: remainingBankHolidays,
      passedBHList: passedBankHolidays,

      // Total
      totalTimeOff
    };
  }, [proRataStartDate, leaveYear, leaveSettings, bhList]);

  // Part-time calculation — statutory numbers from the backend rule pack only.
  const partTimeResult = useMemo(() => {
    if (!annualLeaveRule) return null;
    const daysWorked = Math.min(Math.max(partTimeDays, 1), 7);
    const weeklyEntitlement = annualLeaveRule.weeks;
    const cap = annualLeaveRule.cap_days;
    const annualEntitlement = Math.min(daysWorked * weeklyEntitlement, cap);
    const percentage = ((annualEntitlement / cap) * 100).toFixed(0);

    return {
      daysWorked,
      annualEntitlement: Math.round(annualEntitlement * 2) / 2,
      percentage,
      weeklyEntitlement,
      cap
    };
  }, [partTimeDays, annualLeaveRule]);

  // Leaver calculation
  const leaverResult = useMemo(() => {
    if (!leavingDate) return null;
    
    const leaving = new Date(leavingDate);
    const { yearStart, yearEnd } = leaveYear;
    
    if (leaving < yearStart) {
      return { error: 'Leaving date must be within or after the current leave year start' };
    }
    
    const totalDaysInYear = Math.round((yearEnd - yearStart) / (1000 * 60 * 60 * 24));
    const daysWorked = Math.round((leaving - yearStart) / (1000 * 60 * 60 * 24));
    const proportion = Math.min(daysWorked / totalDaysInYear, 1);
    
    const fullEntitlement = entitlementBreakdown.base;
    const entitledTo = Math.ceil(fullEntitlement * proportion * 2) / 2;
    const taken = daysTaken;
    const balance = entitledTo - taken;
    
    return {
      leavingDate: leaving.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
      proportion: (proportion * 100).toFixed(1),
      entitledTo,
      taken,
      balance,
      owedOrOwing: balance >= 0 ? 'owed' : 'owing'
    };
  }, [leavingDate, daysTaken, leaveYear, entitlementBreakdown]);

  // Check if current settings meet the statutory minimum (null = no rule pack =
  // no check rendered at all).
  const statutoryCheck = useMemo(() => {
    if (statutoryMinimum === null) return null;
    const total = entitlementBreakdown.base + (leaveSettings?.bank_holidays_included ? 0 : bankHolidayCount);
    const meetsMinimum = total >= statutoryMinimum;
    const difference = total - statutoryMinimum;

    return {
      meetsMinimum,
      total,
      minimum: statutoryMinimum,
      difference,
      percentageAbove: ((total / statutoryMinimum - 1) * 100).toFixed(0)
    };
  }, [entitlementBreakdown, leaveSettings, bankHolidayCount, statutoryMinimum]);

  // Irregular hours state
  const [irregularHours, setIrregularHours] = useState({
    hoursWorked: 0,
    payPeriod: 'week', // week, month, year
    hourlyRate: 0, // optional for pay calculation
  });

  // Irregular hours accrual — the rate is DERIVED from the jurisdiction's weeks:
  // weeks / (52 - weeks). For the UK that's 5.6 / 46.4 = 12.07%, but it's the
  // rule pack's number, not a hard-coded one. No rule pack = tab hidden.
  const irregularAccrualRate = annualLeaveRule
    ? annualLeaveRule.weeks / (52 - annualLeaveRule.weeks)
    : null;
  const irregularResult = useMemo(() => {
    if (irregularAccrualRate === null) return null;
    const hours = irregularHours.hoursWorked || 0;
    const accrualRate = irregularAccrualRate;
    
    let hoursPerPeriod = hours;
    let periodLabel = 'this period';
    let annualizedHours = hours;
    
    switch (irregularHours.payPeriod) {
      case 'week':
        annualizedHours = hours * 52;
        periodLabel = 'this week';
        break;
      case 'month':
        annualizedHours = hours * 12;
        periodLabel = 'this month';
        break;
      case 'year':
        annualizedHours = hours;
        periodLabel = 'this year';
        break;
    }
    
    const holidayHoursAccrued = hoursPerPeriod * accrualRate;
    const annualHolidayHours = annualizedHours * accrualRate;
    
    // Convert to days (assuming 8-hour day)
    const holidayDaysAccrued = holidayHoursAccrued / 8;
    const annualHolidayDays = annualHolidayHours / 8;
    
    // Holiday pay calculation (if hourly rate provided)
    const holidayPay = irregularHours.hourlyRate > 0 
      ? holidayHoursAccrued * irregularHours.hourlyRate 
      : null;
    
    return {
      hoursWorked: hours,
      periodLabel,
      holidayHoursAccrued: holidayHoursAccrued.toFixed(2),
      holidayDaysAccrued: holidayDaysAccrued.toFixed(2),
      annualHolidayHours: annualHolidayHours.toFixed(1),
      annualHolidayDays: annualHolidayDays.toFixed(1),
      holidayPay: holidayPay ? holidayPay.toFixed(2) : null,
      accrualRate: (accrualRate * 100).toFixed(2),
    };
  }, [irregularHours, irregularAccrualRate]);

  // Purely statutory tabs (part-time entitlement, irregular-hours accrual) only
  // exist when we have a rule pack for this tenant's country (D9). Breakdown,
  // pro-rata and leaver work from the COMPANY'S OWN policy, so they always show.
  const tabs = [
    { id: 'breakdown', label: 'Entitlement Breakdown', icon: Calculator },
    { id: 'prorata', label: 'Pro-Rata', icon: Calendar },
    ...(annualLeaveRule ? [
      { id: 'parttime', label: 'Part-Time', icon: Clock },
      { id: 'irregular', label: 'Irregular Hours', icon: TrendingUp },
    ] : []),
    { id: 'leaver', label: 'Leaver', icon: UserMinus },
  ];

  return (
    <div className="card-hive border-primary/30 overflow-hidden">
      {/* Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-6 flex items-center justify-between hover:bg-secondary/30 transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-purple-500/20 flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-primary" />
          </div>
          <div className="text-left">
            <h2 className="font-heading font-bold text-lg flex items-center gap-2">
              Leave Calculator
              <span className="text-xs font-normal px-2 py-0.5 rounded-full bg-primary/20 text-primary">
                Interactive
              </span>
            </h2>
            <p className="text-sm text-muted-foreground">
              Calculate entitlements, pro-rata, part-time adjustments & more
            </p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-muted-foreground" />
        ) : (
          <ChevronDown className="w-5 h-5 text-muted-foreground" />
        )}
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-border">
          {/* Tab Navigation */}
          <div className="flex border-b border-border overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
                  activeTab === tab.id
                    ? 'border-primary text-primary bg-primary/5'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-secondary/50'
                }`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {/* Entitlement Breakdown Tab */}
            {activeTab === 'breakdown' && (
              <div className="space-y-6">
                {/* Main Breakdown Card */}
                <div className="grid gap-4 lg:grid-cols-2">
                  {/* Calculation Stack */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                      How Your Entitlement is Calculated
                    </h3>
                    
                    <div className="space-y-2">
                      {/* Base Allowance */}
                      <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                            <Calendar className="w-4 h-4 text-blue-500" />
                          </div>
                          <span className="font-medium">Base Allowance</span>
                        </div>
                        <span className="font-bold text-lg">{entitlementBreakdown.base}</span>
                      </div>

                      {/* Bank Holidays */}
                      <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
                            <Briefcase className="w-4 h-4 text-red-500" />
                          </div>
                          <div>
                            <span className="font-medium">Bank Holidays</span>
                            <p className="text-xs text-muted-foreground">
                              {leaveSettings?.bank_holidays_included ? 'Included in base' : 'Additional to base'}
                            </p>
                          </div>
                        </div>
                        <span className="font-bold text-lg text-green-500">
                          +{entitlementBreakdown.bankHolidays}
                        </span>
                      </div>

                      {/* Service Bonus */}
                      {leaveSettings?.service_bonus_enabled && (
                        <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                              <Award className="w-4 h-4 text-amber-500" />
                            </div>
                            <div>
                              <span className="font-medium">Service Bonus</span>
                              <p className="text-xs text-muted-foreground">Example: 3 years service</p>
                            </div>
                          </div>
                          <span className="font-bold text-lg text-green-500">
                            +{entitlementBreakdown.serviceBonus}
                          </span>
                        </div>
                      )}

                      {/* Carry Over */}
                      {leaveSettings?.carry_over_enabled && (
                        <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-purple-500/20 flex items-center justify-center">
                              <RefreshCw className="w-4 h-4 text-purple-500" />
                            </div>
                            <div>
                              <span className="font-medium">Carry Over</span>
                              <p className="text-xs text-muted-foreground">From previous year</p>
                            </div>
                          </div>
                          <span className="font-bold text-lg text-green-500">
                            +{entitlementBreakdown.carryOver}
                          </span>
                        </div>
                      )}

                      {/* Divider */}
                      <div className="border-t border-dashed border-border my-2" />

                      {/* Total */}
                      <div className="flex items-center justify-between p-4 rounded-lg bg-primary/10 border border-primary/30">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                            <Sparkles className="w-5 h-5 text-primary" />
                          </div>
                          <span className="font-bold text-lg">Total Entitlement</span>
                        </div>
                        <span className="font-bold text-2xl text-primary">
                          {entitlementBreakdown.total} days
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Statutory Check & Visual — only when we have a rule pack for
                      this tenant's country (D9). No pack = the section vanishes. */}
                  {statutoryCheck && (
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                      Statutory Compliance
                    </h3>

                    {/* Status Card */}
                    <div className={`p-4 rounded-lg border ${
                      statutoryCheck.meetsMinimum
                        ? 'bg-green-500/10 border-green-500/30'
                        : 'bg-red-500/10 border-red-500/30'
                    }`}>
                      <div className="flex items-start gap-3">
                        {statutoryCheck.meetsMinimum ? (
                          <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
                        ) : (
                          <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0" />
                        )}
                        <div>
                          <p className={`font-bold ${statutoryCheck.meetsMinimum ? 'text-green-500' : 'text-red-500'}`}>
                            {statutoryCheck.meetsMinimum
                              ? `Meets ${jurisdictionName} Statutory Minimum`
                              : `Below ${jurisdictionName} Statutory Minimum`}
                          </p>
                          <p className="text-sm text-muted-foreground mt-1">
                            {statutoryCheck.meetsMinimum
                              ? `${statutoryCheck.percentageAbove}% above the ${statutoryCheck.minimum}-day minimum (${statutoryCheck.difference} extra days)`
                              : `${Math.abs(statutoryCheck.difference)} days below the required minimum`}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Visual Progress */}
                    <div className="p-4 rounded-lg bg-secondary/50">
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-muted-foreground">Entitlement vs Statutory</span>
                        <span className="font-medium">{statutoryCheck.total} / {statutoryCheck.minimum} days</span>
                      </div>
                      <div className="h-4 bg-secondary rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            statutoryCheck.meetsMinimum ? 'bg-gradient-to-r from-green-500 to-emerald-400' : 'bg-red-500'
                          }`}
                          style={{ width: `${Math.min((statutoryCheck.total / statutoryCheck.minimum) * 100, 150)}%` }}
                        />
                      </div>
                      <div className="flex justify-between text-xs text-muted-foreground mt-2">
                        <span>0</span>
                        <span className="text-amber-500">{statutoryCheck.minimum} (min)</span>
                        <span>{Math.max(statutoryCheck.total, 40)}</span>
                      </div>
                    </div>

                    {/* Info Box */}
                    <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                      <div className="flex gap-2">
                        <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-blue-300">
                          {jurisdictionName} full-time workers are entitled to at least {annualLeaveRule.weeks} weeks
                          ({annualLeaveRule.cap_days} days) paid holiday.
                          {jurisdiction?.public_holidays_may_be_included && ' This can include bank holidays at the employer\'s discretion.'}
                          {annualLeaveRule.source_url && (
                            <> <a href={annualLeaveRule.source_url} target="_blank" rel="noreferrer" className="underline hover:text-blue-200">source</a></>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                  )}
                </div>
              </div>
            )}

            {/* Pro-Rata Calculator Tab */}
            {activeTab === 'prorata' && (
              <div className="space-y-6">
                <div className="grid gap-6 lg:grid-cols-2">
                  {/* Input */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                      Calculate Pro-Rata Entitlement
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      For employees starting part-way through the leave year
                    </p>
                    
                    <div className="p-4 rounded-lg bg-secondary/50 space-y-4">
                      <div>
                        <Label>Employee Start Date</Label>
                        <Input
                          type="date"
                          value={proRataStartDate}
                          onChange={(e) => setProRataStartDate(e.target.value)}
                          className="input-hive mt-1"
                          min={leaveYear.yearStart.toISOString().split('T')[0]}
                          max={leaveYear.yearEnd.toISOString().split('T')[0]}
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Leave year: {leaveYear.yearStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} - {leaveYear.yearEnd.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Result */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                      Result
                    </h3>
                    
                    {proRataResult ? (
                      proRataResult.error ? (
                        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                          <p className="text-red-400">{proRataResult.error}</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {/* Total Time Off - Main Result */}
                          <div className="p-4 rounded-lg bg-gradient-to-br from-primary/20 to-purple-500/20 border border-primary/30">
                            <p className="text-sm text-muted-foreground">Total Time Off</p>
                            <p className="text-4xl font-bold text-primary mt-1">
                              {proRataResult.totalTimeOff} days
                            </p>
                            <p className="text-sm text-muted-foreground mt-1">
                              For employee starting {proRataResult.startDateFormatted}
                            </p>
                          </div>

                          {/* Breakdown */}
                          <div className="space-y-2">
                            {/* Base Pro-Rata */}
                            <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                                  <Calendar className="w-4 h-4 text-blue-500" />
                                </div>
                                <div>
                                  <span className="font-medium">Base Allowance (Pro-Rata)</span>
                                  <p className="text-xs text-muted-foreground">
                                    {proRataResult.monthsRemaining}/12 months × {proRataResult.fullEntitlement} days
                                  </p>
                                </div>
                              </div>
                              <span className="font-bold text-lg">{proRataResult.proRataEntitlement}</span>
                            </div>

                            {/* Bank Holidays */}
                            {!proRataResult.bankHolidaysIncluded && (
                              <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 rounded-lg bg-red-500/20 flex items-center justify-center">
                                    <Briefcase className="w-4 h-4 text-red-500" />
                                  </div>
                                  <div>
                                    <span className="font-medium">Bank Holidays (Remaining)</span>
                                    <p className="text-xs text-muted-foreground">
                                      {proRataResult.passedBankHolidays} passed, {proRataResult.remainingBankHolidays} remaining
                                    </p>
                                  </div>
                                </div>
                                <span className="font-bold text-lg text-green-500">+{proRataResult.remainingBankHolidays}</span>
                              </div>
                            )}

                            {/* Divider */}
                            <div className="border-t border-dashed border-border my-1" />

                            {/* Total */}
                            <div className="flex items-center justify-between p-3 rounded-lg bg-primary/10 border border-primary/20">
                              <span className="font-bold">Total</span>
                              <span className="font-bold text-xl text-primary">{proRataResult.totalTimeOff} days</span>
                            </div>
                          </div>

                          {/* Calculation Method Info */}
                          <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                            <div className="flex gap-2">
                              <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                              <div className="text-xs text-blue-300">
                                <p className="font-medium mb-1">Calculation method (monthly accrual, rounded up)</p>
                                <p>
                                  Monthly accrual: {proRataResult.monthsRemaining} months ÷ 12 = {proRataResult.monthlyProportion}% 
                                  of {proRataResult.fullEntitlement} days = {proRataResult.proRataBase} → <strong>rounded up to {proRataResult.proRataEntitlement}</strong>
                                </p>
                                {!proRataResult.bankHolidaysIncluded && proRataResult.passedBankHolidays > 0 && (
                                  <p className="mt-1">
                                    Bank holidays missed: {proRataResult.passedBHList.map(bh => bh.name).join(', ')}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Visual Progress */}
                          <div className="p-3 rounded-lg bg-secondary/30">
                            <div className="flex items-center gap-2 text-sm mb-2">
                              <span className="text-muted-foreground text-xs">Year Progress</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-3 bg-secondary rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-gradient-to-r from-primary to-purple-500 rounded-full"
                                  style={{ width: `${proRataResult.monthlyProportion}%` }}
                                />
                              </div>
                              <span className="text-muted-foreground text-xs w-16 text-right">{proRataResult.monthlyProportion}%</span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-2 text-center">
                              {proRataResult.monthsRemaining} of 12 months remaining
                            </p>
                          </div>
                        </div>
                      )
                    ) : (
                      <div className="p-8 rounded-lg bg-secondary/30 text-center">
                        <Calendar className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                        <p className="text-muted-foreground">Enter a start date to calculate</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Part-Time Calculator Tab (statutory — only exists with a rule pack) */}
            {activeTab === 'parttime' && partTimeResult && (
              <div className="space-y-6">
                <div className="grid gap-6 lg:grid-cols-2">
                  {/* Input */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                      Part-Time Entitlement
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Calculate statutory entitlement based on days worked per week
                    </p>
                    
                    <div className="p-4 rounded-lg bg-secondary/50 space-y-4">
                      <div>
                        <Label>Days Worked Per Week</Label>
                        <div className="flex items-center gap-4 mt-2">
                          <input
                            type="range"
                            min="1"
                            max="7"
                            step="1"
                            value={partTimeDays}
                            onChange={(e) => setPartTimeDays(parseInt(e.target.value))}
                            className="flex-1 h-2 bg-secondary rounded-full appearance-none cursor-pointer accent-primary"
                          />
                          <span className="w-12 h-10 rounded-lg bg-primary/20 flex items-center justify-center font-bold text-primary">
                            {partTimeDays}
                          </span>
                        </div>
                        <div className="flex justify-between text-xs text-muted-foreground mt-1">
                          <span>1 day</span>
                          <span>7 days</span>
                        </div>
                      </div>

                      <div className="p-3 rounded-lg bg-background/50">
                        <p className="text-sm">
                          <span className="text-muted-foreground">Formula: </span>
                          <span className="font-mono">{partTimeDays} days × {partTimeResult.weeklyEntitlement} weeks = </span>
                          <span className="font-bold text-primary">{partTimeResult.annualEntitlement} days</span>
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Result */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                      Statutory Entitlement
                    </h3>
                    
                    <div className="space-y-3">
                      <div className="p-4 rounded-lg bg-primary/10 border border-primary/30">
                        <p className="text-sm text-muted-foreground">Annual Entitlement</p>
                        <p className="text-3xl font-bold text-primary mt-1">
                          {partTimeResult.annualEntitlement} days
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          ({partTimeResult.weeklyEntitlement} weeks)
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="p-3 rounded-lg bg-secondary/50">
                          <p className="text-xs text-muted-foreground">Days Per Week</p>
                          <p className="font-bold text-lg">{partTimeResult.daysWorked}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-secondary/50">
                          <p className="text-xs text-muted-foreground">vs Full-Time</p>
                          <p className="font-bold text-lg">{partTimeResult.percentage}%</p>
                        </div>
                      </div>

                      {/* Visual comparison */}
                      <div className="p-4 rounded-lg bg-secondary/30 space-y-3">
                        <p className="text-sm font-medium">Comparison to Full-Time ({partTimeResult.cap} days)</p>
                        <div className="space-y-2">
                          <div className="flex items-center gap-3">
                            <span className="text-xs w-20 text-muted-foreground">Part-Time</span>
                            <div className="flex-1 h-4 bg-secondary rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-gradient-to-r from-primary to-purple-500 rounded-full"
                                style={{ width: `${partTimeResult.percentage}%` }}
                              />
                            </div>
                            <span className="text-sm font-medium w-16">{partTimeResult.annualEntitlement}d</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-xs w-20 text-muted-foreground">Full-Time</span>
                            <div className="flex-1 h-4 bg-secondary rounded-full overflow-hidden">
                              <div className="h-full bg-muted-foreground/30 rounded-full w-full" />
                            </div>
                            <span className="text-sm font-medium w-16 text-muted-foreground">{partTimeResult.cap}d</span>
                          </div>
                        </div>
                      </div>

                      {partTimeDays >= 6 && (
                        <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                          <div className="flex gap-2">
                            <Info className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-amber-300">
                              Statutory leave is capped at {partTimeResult.cap} days, even for those working 6-7 days per week.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Irregular Hours Calculator Tab (statutory — only exists with a rule pack) */}
            {activeTab === 'irregular' && irregularResult && (
              <div className="space-y-6">
                <div className="grid gap-6 lg:grid-cols-2">
                  {/* Input */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                      Irregular Hours Accrual
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      For zero-hours contracts, casual workers, or variable shift patterns
                    </p>
                    
                    <div className="p-4 rounded-lg bg-secondary/50 space-y-4">
                      <div>
                        <Label>Hours Worked</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.5"
                          value={irregularHours.hoursWorked || ''}
                          onChange={(e) => setIrregularHours({
                            ...irregularHours,
                            hoursWorked: parseFloat(e.target.value) || 0
                          })}
                          className="input-hive mt-1"
                          placeholder="Enter hours worked"
                        />
                      </div>
                      
                      <div>
                        <Label>Time Period</Label>
                        <select
                          value={irregularHours.payPeriod}
                          onChange={(e) => setIrregularHours({
                            ...irregularHours,
                            payPeriod: e.target.value
                          })}
                          className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
                        >
                          <option value="week">Per Week</option>
                          <option value="month">Per Month</option>
                          <option value="year">Per Year</option>
                        </select>
                      </div>

                      <div>
                        <Label>Hourly Rate (optional, for pay calculation)</Label>
                        <div className="relative mt-1">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={irregularHours.hourlyRate || ''}
                            onChange={(e) => setIrregularHours({
                              ...irregularHours,
                              hourlyRate: parseFloat(e.target.value) || 0
                            })}
                            className="input-hive pl-7"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                      <div className="flex gap-2">
                        <Info className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                        <div className="text-xs text-blue-300">
                          <p className="font-medium mb-1">How {irregularResult.accrualRate}% works:</p>
                          <p>
                            Irregular hours workers accrue holiday at {irregularResult.accrualRate}% of hours worked.
                            This equals {annualLeaveRule.weeks} weeks ÷ {52 - annualLeaveRule.weeks} working
                            weeks (52 weeks minus {annualLeaveRule.weeks} weeks holiday).
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Result */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                      Holiday Accrued
                    </h3>
                    
                    {irregularHours.hoursWorked > 0 ? (
                      <div className="space-y-3">
                        {/* Period Accrual */}
                        <div className="p-4 rounded-lg bg-primary/10 border border-primary/30">
                          <p className="text-sm text-muted-foreground">For {irregularResult.periodLabel}</p>
                          <div className="flex items-baseline gap-2 mt-1">
                            <span className="text-3xl font-bold text-primary">
                              {irregularResult.holidayHoursAccrued}
                            </span>
                            <span className="text-lg text-muted-foreground">hours</span>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            ({irregularResult.holidayDaysAccrued} days at 8hrs/day)
                          </p>
                        </div>

                        {/* Calculation breakdown */}
                        <div className="p-3 rounded-lg bg-secondary/50">
                          <p className="text-sm font-medium mb-2">Calculation</p>
                          <div className="font-mono text-sm">
                            <span className="text-muted-foreground">{irregularResult.hoursWorked} hrs</span>
                            <span className="text-muted-foreground"> × </span>
                            <span className="text-primary">{irregularResult.accrualRate}%</span>
                            <span className="text-muted-foreground"> = </span>
                            <span className="font-bold">{irregularResult.holidayHoursAccrued} hrs</span>
                          </div>
                        </div>

                        {/* Annualized projection */}
                        <div className="p-3 rounded-lg bg-secondary/50">
                          <p className="text-sm text-muted-foreground">If this continues for a full year:</p>
                          <p className="font-bold text-lg">
                            {irregularResult.annualHolidayHours} hours 
                            <span className="text-muted-foreground font-normal text-sm ml-1">
                              ({irregularResult.annualHolidayDays} days)
                            </span>
                          </p>
                        </div>

                        {/* Holiday pay if hourly rate provided */}
                        {irregularResult.holidayPay && (
                          <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                            <p className="text-sm text-muted-foreground">Holiday Pay Accrued {irregularResult.periodLabel}</p>
                            <p className="text-2xl font-bold text-green-500 mt-1">
                              £{irregularResult.holidayPay}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Based on £{irregularHours.hourlyRate}/hour
                            </p>
                          </div>
                        )}

                        {/* Visual */}
                        <div className="p-3 rounded-lg bg-secondary/30">
                          <p className="text-xs text-muted-foreground mb-2">Accrual Rate Visualization</p>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-4 bg-secondary rounded-full overflow-hidden">
                              <div
                                className="h-full bg-gradient-to-r from-primary to-purple-500 rounded-full"
                                style={{ width: `${irregularResult.accrualRate}%` }}
                              />
                            </div>
                            <span className="text-xs font-medium">{irregularResult.accrualRate}%</span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-2 text-center">
                            For every 100 hours worked, {(100 * irregularAccrualRate).toFixed(1)} holiday hours accrue
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="p-8 rounded-lg bg-secondary/30 text-center">
                        <TrendingUp className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                        <p className="text-muted-foreground">Enter hours worked to calculate</p>
                      </div>
                    )}
                  </div>
                </div>

              </div>
            )}

            {/* Leaver Calculator Tab */}
            {activeTab === 'leaver' && (
              <div className="space-y-6">
                <div className="grid gap-6 lg:grid-cols-2">
                  {/* Input */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                      Leaver Entitlement
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      Calculate what an employee is owed (or owes) when leaving
                    </p>
                    
                    <div className="p-4 rounded-lg bg-secondary/50 space-y-4">
                      <div>
                        <Label>Leaving Date</Label>
                        <Input
                          type="date"
                          value={leavingDate}
                          onChange={(e) => setLeavingDate(e.target.value)}
                          className="input-hive mt-1"
                        />
                      </div>
                      
                      <div>
                        <Label>Days Already Taken This Year</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.5"
                          value={daysTaken}
                          onChange={(e) => setDaysTaken(parseFloat(e.target.value) || 0)}
                          className="input-hive mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Result */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                      Settlement
                    </h3>
                    
                    {leaverResult ? (
                      leaverResult.error ? (
                        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                          <p className="text-red-400">{leaverResult.error}</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className={`p-4 rounded-lg border ${
                            leaverResult.balance >= 0 
                              ? 'bg-green-500/10 border-green-500/30' 
                              : 'bg-amber-500/10 border-amber-500/30'
                          }`}>
                            <p className="text-sm text-muted-foreground">
                              {leaverResult.balance >= 0 ? 'Employee is Owed' : 'Employee Owes'}
                            </p>
                            <p className={`text-3xl font-bold mt-1 ${
                              leaverResult.balance >= 0 ? 'text-green-500' : 'text-amber-500'
                            }`}>
                              {Math.abs(leaverResult.balance)} days
                            </p>
                            <p className="text-sm text-muted-foreground mt-1">
                              {leaverResult.balance >= 0 
                                ? 'Payment in lieu required'
                                : 'Can be deducted from final pay (if agreed in contract)'}
                            </p>
                          </div>

                          <div className="space-y-2">
                            <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                              <span className="text-muted-foreground">Leaving Date</span>
                              <span className="font-medium">{leaverResult.leavingDate}</span>
                            </div>
                            <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                              <span className="text-muted-foreground">Year Worked</span>
                              <span className="font-medium">{leaverResult.proportion}%</span>
                            </div>
                            <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                              <span className="text-muted-foreground">Entitled To</span>
                              <span className="font-medium">{leaverResult.entitledTo} days</span>
                            </div>
                            <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/50">
                              <span className="text-muted-foreground">Already Taken</span>
                              <span className="font-medium">{leaverResult.taken} days</span>
                            </div>
                          </div>

                          {/* Visual */}
                          <div className="p-3 rounded-lg bg-secondary/30">
                            <div className="flex items-center gap-2 text-sm mb-2">
                              <span className="text-muted-foreground">Entitled</span>
                              <ArrowRight className="w-4 h-4 text-muted-foreground" />
                              <span className="text-muted-foreground">Taken</span>
                              <ArrowRight className="w-4 h-4 text-muted-foreground" />
                              <span className={leaverResult.balance >= 0 ? 'text-green-500' : 'text-amber-500'}>
                                Balance
                              </span>
                            </div>
                            <div className="flex items-center gap-2 font-bold">
                              <span>{leaverResult.entitledTo}</span>
                              <span className="text-muted-foreground">−</span>
                              <span>{leaverResult.taken}</span>
                              <span className="text-muted-foreground">=</span>
                              <span className={leaverResult.balance >= 0 ? 'text-green-500' : 'text-amber-500'}>
                                {leaverResult.balance >= 0 ? '+' : ''}{leaverResult.balance}
                              </span>
                            </div>
                          </div>
                        </div>
                      )
                    ) : (
                      <div className="p-8 rounded-lg bg-secondary/30 text-center">
                        <UserMinus className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                        <p className="text-muted-foreground">Enter leaving details to calculate</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LeaveCalculator;
